import { useEffect, useRef } from "react";

export default function Spinner({ color = "#1a9faf", size }: { color?: string, size?: number }) {
    const canvas = useRef<HTMLCanvasElement>(null)

    useEffect(() => {
        // On the first render, canvas won't be set. However, once setCanvas is called by ref, this effect will run again.
        if (canvas.current == null) return;
        const ctx = canvas.current.getContext("2d");
        if (ctx == null) return;

        const angularVelocityRadsPerSec = 2 * Math.PI;
        const distancePeriodMs = 10000;
        const intrinsicAspect = 868 / 1200;
        const canvasAspect = canvas.current.width / canvas.current.height;
        const width = canvasAspect > intrinsicAspect ? canvas.current.height * intrinsicAspect : canvas.current.width;
        const height = width / intrinsicAspect;
        const lineWidth = height / 9.5;
        const circleRadius = (width - lineWidth) / 2;
        const maxDistance = (width - circleRadius - lineWidth) / 2;
        const dotRadius = lineWidth * 1.4 / 2;

        let angle = 0;
        let distanceProgress = 0;

        let lastTimestamp: DOMHighResTimeStamp | undefined = undefined;

        ctx.strokeStyle = ctx.fillStyle = color;
        ctx.lineWidth = lineWidth;
        
        // Keep track of wether the component is still mounted
        let mounted = true;

        const loop = (timestamp: DOMHighResTimeStamp) => {
            if (!mounted) return;
            if (!canvas.current) return;

            const elapsedMs = lastTimestamp ? timestamp - lastTimestamp : 0;
            angle = (angle + elapsedMs * angularVelocityRadsPerSec / 1000) % (2 * Math.PI);
            distanceProgress = (distanceProgress + elapsedMs / distancePeriodMs) % 1;

            lastTimestamp = timestamp;
            
            ctx.clearRect(0, 0, canvas.current.width, canvas.current.height); // clear canvas for next frame
            const distanceFactor = Math.sin(distanceProgress * 2 * Math.PI);
            const distance = maxDistance * distanceFactor * distanceFactor;
            const distanceX = distance * Math.sin(angle);
            const distanceY = distance * Math.cos(angle);

            ctx.beginPath();
            ctx.ellipse(canvas.current!.width / 2 + distanceX, canvas.current!.height / 2 + distanceY, circleRadius, circleRadius, 0, 0, 2 * Math.PI);
            ctx.stroke();

            ctx.beginPath();
            ctx.ellipse(canvas.current!.width / 2 - distanceX, canvas.current!.height / 2 - distanceY, circleRadius, circleRadius, 0, 0, 2 * Math.PI);
            ctx.stroke();

            ctx.beginPath();
            ctx.ellipse(canvas.current!.width / 2, canvas.current!.height / 2, dotRadius, dotRadius, 0, 0, 2 * Math.PI);
            ctx.fill();

            requestAnimationFrame(loop); // animate
        };

        requestAnimationFrame(loop);

        // React will call this function when the effects' dependencies change, or the component is unmounted
        return () => {
            // Set mounted to false so that the next frame is short-circuited.
            mounted = false;
        };
    }, [canvas,color]);

    const canvasSize = size ? size * 4 : 630
    return <canvas width={canvasSize} height={canvasSize} ref={canvas} style={size ? { width: size, height: size } : undefined}></canvas>;
}